import React, { useState, useEffect, useRef } from 'react';
import { Check, Cloud, Loader, AlertTriangle } from 'lucide-react';
import { fullDataSyncHandler } from '../../services/dataPersistence/fullDataSyncHandler';
import { SyncState } from '../../services/dataPersistence/autoSyncService';
import { conversationService} from '../../services/dataPersistence';


// Use this component in your header
const InlineStatusIndicator = () => {
  const [syncState, setSyncState] = useState<SyncState>(fullDataSyncHandler.getSyncStatus());
  const [showText, setShowText] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const timerRef = useRef<number | null>(null);

  const [isRebuilding, setIsRebuilding] = useState(false);

  
  useEffect(() => {
    const handleRebuildStart = () => setIsRebuilding(true);
    const handleRebuildComplete = () => setIsRebuilding(false);
    
    window.addEventListener('tripper-chat-loading', handleRebuildStart);
    window.addEventListener('tripper-rebuild-complete', handleRebuildComplete);
    
    return () => {
      window.removeEventListener('tripper-chat-loading', handleRebuildStart);
      window.removeEventListener('tripper-rebuild-complete', handleRebuildComplete);
    };
  }, []);

  // Modify the isNewUser detection to be more aggressive
  useEffect(() => {
    // Check for signs this is a new user
    const accountJustCreated = localStorage.getItem('tripperAccountJustCreated') === 'true';
    const recentAccountCreation = sessionStorage.getItem('tripperAccountCreated') === 'true';
    const noConversationRestored = localStorage.getItem('conversation_restored') !== 'true';
    const lowMessageCount = conversationService.getConversationSequence().length < 3;
    
    // More aggressive checks - any of these can indicate a new user
    const isNew = accountJustCreated || 
                  recentAccountCreation || 
                  (noConversationRestored && lowMessageCount);
    
    if (isNew) {
      console.log('[SaveIndicator] Detected new user, suppressing error messages');
      setIsNewUser(true);
      
      // Clear the "just created" flag after a longer time for new users
      setTimeout(() => {
        setIsNewUser(false);
        localStorage.removeItem('tripperAccountJustCreated');
      }, 300000); // 5 minutes - much longer grace period
    }
  }, []);

  
  // Debug output
  useEffect(() => {
    console.log('[SaveIndicator] Current state:', syncState, 'New user:', isNewUser);
  }, [syncState, isNewUser]);
  
  useEffect(() => {
    const handleSyncUpdate = (event: CustomEvent<SyncState>) => {
      if (isRebuilding) {
        console.log('[SaveIndicator] Ignoring sync update during rebuild');
        return;
      }
      const newState = event.detail || fullDataSyncHandler.getSyncStatus();
      console.log('[SaveIndicator] Received sync update:', newState);
      setSyncState(newState);
      
      // Clear any existing timer
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      
      // For new users, don't show error text
      if (isNewUser && newState.status === 'error') {
        setShowText(false);
        return;
      }
      
      // Show text for any status change
      setShowText(true);
      
      // Auto-hide success/error messages after delay
      if (newState.status === 'success' || newState.status === 'error') {
        timerRef.current = window.setTimeout(() => {
          setShowText(false);
          timerRef.current = null;
        }, 30000);
      }
    };
    
    // Direct listener for reliable updates
    const directListener = (state: SyncState) => {
      console.log('[SaveIndicator] Direct listener update:', state);
      setSyncState(state);
      
      // Clear any existing timer
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      
      // For new users, don't show error text
      if (isNewUser && state.status === 'error') {
        setShowText(false);
        return;
      }
      
      // Show text for any status change
      setShowText(true);
      
      // Auto-hide success/error messages after delay
      if (state.status === 'success' || state.status === 'error') {
        timerRef.current = window.setTimeout(() => {
          setShowText(false);
          timerRef.current = null;
        }, 30000);
      }
    };
    
    // Add both event listeners
    window.addEventListener('tripper-sync-status-updated', handleSyncUpdate as EventListener);
    fullDataSyncHandler.addStatusListener(directListener);
    
    // Initial status check
    const initialState = fullDataSyncHandler.getSyncStatus();
    if (initialState.status !== 'syncing') {
      setSyncState(initialState);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('tripper-sync-status-updated', handleSyncUpdate as EventListener);
      fullDataSyncHandler.removeStatusListener(directListener);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isNewUser]); // Added isNewUser as dependency
  
  /* // Manual sync handler
  const handleManualSync = () => {
    if (syncState.status !== 'syncing') {
      console.log('[SaveIndicator] Triggering manual sync');
      fullDataSyncHandler.syncAllData();
    }
  }; */

  // Modify handleManualSync
  const handleManualSync = () => {
    if (isRebuilding) {
      console.log('[SaveIndicator] Skipping sync - rebuild in progress');
      return;
    }
    
    if (syncState.status !== 'syncing') {
      console.log('[SaveIndicator] Triggering manual sync');
      fullDataSyncHandler.syncAllData();
    }
  };
  
  // Icon and text based on sync state
  const getIconAndText = () => {
    // For new users with error state, show the idle cloud instead
    if (isNewUser && syncState.status === 'error') {
      return {
        icon: <Cloud size={16} className="text-gray-400" />,
        text: '',
        color: 'text-gray-500'
      };
    }
    
    switch (syncState.status) {
      case 'syncing':
        return {
          icon: <Loader className="animate-spin" size={16} />,
          text: 'Saving...',
          color: 'text-blue-500'
        };
      case 'success':
        return {
          icon: <Check size={16} className="text-green-500" />,
          text: showText ? 'Saved' : '',
          color: 'text-green-500'
        };
      case 'error':
        return {
          icon: <AlertTriangle size={16} className="text-red-500" />,
          text: showText ? 'Yikes!!' : '',
          color: 'text-red-500'
        };
      case 'idle':
      default:
        return {
          icon: <Cloud size={16} className="text-gray-400" />,
          text: '',
          color: 'text-gray-500'
        };
    }
  };
  
  const { icon, text, color } = getIconAndText();
  
  return (
    <button 
      onClick={handleManualSync}
      className={`flex items-center bg-white/90 py-1 px-3 rounded-full text-sm ${color}`}
      title={syncState.error && !isNewUser ? syncState.error : 'Click to save changes'}
    >
      <span className="mr-1.5">{icon}</span>
      {text && <span className="transition-opacity duration-300">{text}</span>}
    </button>
  );
};

export default InlineStatusIndicator;